import React from "react";
import { Link } from "react-router-dom";
import useArabicFieldRemap from "../typing/useArabicFieldRemap";

/**
 * MajlisHeader — the mockup's sticky top bar: م mark + المجلس brand
 * (Scheherazade New) linking home, then (ms-auto) the ḥarakāt toggle, the
 * create-post button, the admin flag, and the user's monogram.
 *
 * The search pill is REAL (backed by ?q= on the feed list, filtering within
 * the viewer's segregated feed) and renders only where onSearch is provided
 * — the feed page, not sub-pages.
 *
 * Props: currentUser, isAdmin, showHarakat, onToggleHarakat, onCreate,
 * onOpenAdmin, onSearch/searchValue (the controlled search pill), crumbs —
 * breadcrumb text for sub-pages ("إنشاء منشور"); when set, the search pill,
 * create button and تشكيل toggle are hidden (the submit mockup's minimal
 * header).
 */
export default function MajlisHeader({
  currentUser,
  isAdmin,
  showHarakat,
  onToggleHarakat,
  onCreate,
  onOpenAdmin,
  onSearch = null,
  searchValue = "",
  crumbs = null,
}) {
  // Same physical-key → Arabic-101 remap as the composer, so a learner
  // without an Arabic OS layout can type into search too.
  const { remapKeyDown } = useArabicFieldRemap();
  const initial =
    currentUser?.feed_display_name?.[0] ||
    (currentUser?.handle ? currentUser.handle[0] : "؟");

  return (
    <header
      className="sticky top-0 z-40"
      style={{
        background: "var(--mj-header-bg)",
        backdropFilter: "blur(10px)",
        borderBottom: "1px solid var(--mj-line)",
      }}
    >
      <div className="max-w-[1060px] mx-auto px-4 py-2.5 flex items-center gap-x-4 gap-y-2 flex-wrap">
        {/* explicit exit back to the main app — the brand link alone was
            too implicit ("→" points right = back, in RTL) */}
        <Link
          to="/"
          className="rounded-full px-3 py-1 text-[12.5px] font-plex font-semibold shrink-0"
          style={{ color: "var(--mj-muted)", border: "1px solid var(--mj-line)" }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = "var(--mj-gold)";
            e.currentTarget.style.borderColor = "var(--mj-gold-line)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = "var(--mj-muted)";
            e.currentTarget.style.borderColor = "var(--mj-line)";
          }}
          title="العودة إلى تطبيق مُراجَعة"
        >
          → مُراجَعة
        </Link>

        {/* brand → back to the app */}
        <Link
          to="/"
          className="flex items-center gap-2.5 font-scheherazade font-bold text-2xl"
          style={{ color: "var(--mj-cream)" }}
          title="العودة إلى مُراجَعة"
        >
          <span
            className="w-8 h-8 rounded-full grid place-items-center text-base shrink-0"
            style={{
              background: "linear-gradient(135deg, var(--mj-ink-6), var(--mj-ink-4))",
              border: "1.5px solid var(--mj-gold)",
            }}
            aria-hidden="true"
          >
            م
          </span>
          المجلس
          <span dir="ltr" className="font-cormorant text-xs font-medium hidden min-[480px]:inline" style={{ color: "var(--mj-muted)" }}>
            murājaʿa
          </span>
        </Link>

        {!crumbs && onSearch && (
          <div
            className="flex items-center gap-2.5 rounded-full px-4 py-2 order-last basis-full min-[720px]:order-none min-[720px]:basis-auto min-[720px]:flex-1 min-[720px]:max-w-[540px]"
            style={{ background: "var(--mj-raised)", border: "1px solid var(--mj-line)" }}
          >
            <span aria-hidden="true" className="text-[13px]">🔎</span>
            <input
              dir="rtl"
              lang="ar"
              type="search"
              className="flex-1 bg-transparent outline-none font-plex text-[14px]"
              style={{ color: "var(--mj-cream)" }}
              placeholder="ابحث في المجلس…"
              value={searchValue}
              onChange={(e) => onSearch(e.target.value)}
              onKeyDown={(e) => remapKeyDown(e, onSearch)}
              aria-label="ابحث في المجلس"
            />
          </div>
        )}

        {crumbs && (
          <span className="font-plex text-[13px]" style={{ color: "var(--mj-muted)" }}>
            <Link
              to="/majlis"
              className="hover:brightness-125"
              style={{ color: "var(--mj-muted)" }}
              title="العودة إلى المجلس"
            >
              → المجلس
            </Link>{" "}
            / <b style={{ color: "var(--mj-cream)" }}>{crumbs}</b>
          </span>
        )}

        <div className="flex items-center gap-3 ms-auto">
          {!crumbs && (
          <button
            onClick={onToggleHarakat}
            className="rounded-full px-3 py-1 text-[12.5px] font-plex font-semibold"
            style={
              showHarakat
                ? {
                    background: "var(--mj-gold-soft)",
                    color: "var(--mj-gold)",
                    border: "1px solid var(--mj-gold-line)",
                  }
                : { color: "var(--mj-muted)", border: "1px solid var(--mj-line)" }
            }
            title={showHarakat ? "إخفاء التشكيل" : "إظهار التشكيل"}
            aria-pressed={showHarakat}
          >
            تشكيل
          </button>
          )}
          {isAdmin && onOpenAdmin && (
            <button
              onClick={onOpenAdmin}
              className="p-1.5 rounded-lg"
              style={{ color: "var(--mj-muted)" }}
              title="Feed moderation"
              aria-label="Feed moderation"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 21V4m0 1h13l-2.5 4L17 13H4" />
              </svg>
            </button>
          )}
          {!crumbs && onCreate && (
            <>
              <button
                onClick={onCreate}
                className="hidden min-[560px]:flex items-center gap-1.5 font-plex text-[13.5px] font-semibold rounded-full px-4 py-1.5"
                style={{
                  background: "var(--mj-gold-soft)",
                  color: "var(--mj-gold)",
                  border: "1px solid var(--mj-gold-line)",
                }}
              >
                ＋ أنشئ منشورًا
              </button>
              {/* compact phone version */}
              <button
                onClick={onCreate}
                className="min-[560px]:hidden w-8 h-8 rounded-full grid place-items-center text-base font-bold shrink-0"
                style={{ background: "var(--mj-gold)", color: "var(--mj-bg)" }}
                title="أنشئ منشورًا"
                aria-label="أنشئ منشورًا"
              >
                ＋
              </button>
            </>
          )}
          <span
            className="w-8 h-8 rounded-full grid place-items-center font-plex font-bold text-sm shrink-0"
            style={{
              background: "linear-gradient(135deg, var(--mj-ink-6), var(--mj-ink-4))",
              border: "1.5px solid var(--mj-gold)",
              color: "var(--mj-cream)",
            }}
            aria-hidden="true"
          >
            {initial}
          </span>
        </div>
      </div>
    </header>
  );
}
